import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Seo from '@/seo/Seo';

const contactInfo = [
  {
    icon: 'ri-map-pin-2-line',
    color: 'bg-orange-500',
    title: 'Adres',
    lines: ['Mehmet Abdi Bulut Mh. Fatma Şahin Cd. No:10', 'Merkez / Kilis'],
  },
  {
    icon: 'ri-phone-line',
    color: 'bg-slate-800',
    title: 'Telefon',
    lines: ['0 538 232 38 22', '0 532 453 99 79'],
  },
  {
    icon: 'ri-whatsapp-line',
    color: 'bg-emerald-600',
    title: 'WhatsApp',
    lines: ['0 538 232 38 22'],
  },
  {
    icon: 'ri-time-line',
    color: 'bg-amber-500',
    title: 'Çalışma Saatleri',
    lines: ['Pazartesi - Cumartesi: 08:00 - 18:00', 'Pazar: Kapalı'],
  },
];

export default function ContactPage() {
  return (
    <div className="min-h-screen" style={{ fontFamily: "'Inter', sans-serif" }}>
      <Seo
        title="İletişim | Global79 Lojistik"
        description="Global79 Lojistik iletişim bilgileri: Kilis adresimiz, telefon numaralarımız ve WhatsApp hattımız üzerinden bize ulaşın. Lojistik ihtiyaçlarınız için hızlı destek alın."
        path="/iletisim"
        keywords="Global79 iletişim, Kilis lojistik iletişim, lojistik telefon numarası, WhatsApp lojistik"
        ogImage="/assets/images/logo.png"
      />
      <Navbar />

      {/* Page Hero */}
      <section className="relative h-72 md:h-96 flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=modern%20logistics%20office%20building%20exterior%2C%20professional%20business%20environment%2C%20clean%20architecture%2C%20warm%20lighting%2C%20high%20quality%20commercial%20photography%2C%20ultra%20realistic&width=1920&height=600&seq=conhero01&orientation=landscape"
          alt="İletişim hero"
          className="absolute inset-0 w-full h-full object-cover object-center"
          fetchPriority="high"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />
        <div className="relative z-10 text-center px-4">
          <span className="text-orange-400 text-sm font-bold uppercase tracking-widest mb-3 block">Bize Ulaşın</span>
          <h1 className="text-4xl md:text-6xl font-extrabold text-white" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            İletişim
          </h1>
          <p className="text-gray-300 mt-4 text-base max-w-xl mx-auto">
            Taşımacılık ihtiyaçlarınız için bize ulaşın, size en uygun çözümü sunalım
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Left: Info Cards + Quick Buttons */}
            <div className="w-full lg:w-5/12 flex flex-col gap-5">
              <div>
                <span className="text-orange-500 text-sm font-bold uppercase tracking-widest mb-3 block">İletişim Bilgileri</span>
                <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Sizinle <span className="text-orange-500">Konuşalım</span>
                </h2>
                <p className="text-gray-500 text-sm leading-relaxed mb-6">
                  Lojistik ihtiyaçlarınız için bize her zaman ulaşabilirsiniz. Uzman ekibimiz size en kısa sürede dönüş yapacaktır.
                </p>
              </div>

              {contactInfo.map((info) => (
                <div key={info.title} className="flex items-start gap-4 bg-gray-50 rounded-xl p-5">
                  <div className={`w-12 h-12 flex items-center justify-center ${info.color} rounded-xl flex-shrink-0`}>
                    <i className={`${info.icon} text-white text-xl`} />
                  </div>
                  <div>
                    <div className="font-bold text-gray-800 text-sm mb-1">{info.title}</div>
                    {info.lines.map((line) => (
                      <div key={line} className="text-gray-500 text-sm">{line}</div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Quick Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 mt-2">
                <a
                  href="tel:05382323822"
                  className="flex-1 flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-fill text-lg" />
                  Hemen Ara
                </a>
                <a
                  href="https://wa.me/905382323822?text=Merhaba%2C%20lojistik%20hizmetleriniz%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-4 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-lg" />
                  WhatsApp
                </a>
              </div>
            </div>

            {/* Right: Map */}
            <div className="w-full lg:w-7/12 flex flex-col gap-6">
              <div className="rounded-2xl overflow-hidden flex-1 min-h-[480px]">
                <iframe
                  title="Global79 Lojistik Konum"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3168.0!2d37.1167!3d36.7167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzbCsDQzJzAwLjAiTiAzN8KwMDcnMDAuMCJF!5e0!3m2!1str!2str!4v1680000000000!5m2!1str!2str"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
